import { Color } from "@repo/app-types/constants/colors";
import type {
  PlaybookTestAssertion,
  PlaybookTestAssertionKind,
  PlaybookTestAssertionVerdict,
  PlaybookTestVerdict,
} from "@repo/app-types/entities/playbookTestCase";
import { Size } from "@repo/ui/helpers/helpers";
import { Pill } from "@repo/ui/Pills/pill";
import {
  IconCheck,
  IconChevronDown,
  IconMinus,
  IconX,
} from "@tabler/icons-react";
import type { FC } from "react";
import {
  Button as AriaButton,
  Disclosure,
  DisclosurePanel,
  Heading,
} from "react-aria-components";

/** The claim an assertion makes, in the case's own words. */
export function describePlaybookTestAssertion(
  assertion: PlaybookTestAssertion,
): string {
  switch (assertion.kind) {
    case "outcome":
      return `Outcome is ${assertion.outcome}`;
    case "tool_call": {
      const constraints = (assertion.arguments ?? []).map((argument) => {
        if (argument.equals !== undefined) {
          return `${argument.path} is ${argument.equals}`;
        }
        if (argument.matches !== undefined) {
          return `${argument.path} matches ${argument.matches}`;
        }
        return argument.path;
      });
      const call =
        assertion.expected === "called"
          ? `Calls the ${assertion.tool_id} tool`
          : `Does not call the ${assertion.tool_id} tool`;
      return constraints.length > 0
        ? `${call} with ${constraints.join(", ")}`
        : call;
    }
    case "fact_captured":
      return `Captures ${assertion.fact}`;
    case "content":
      return assertion.expected === "present"
        ? `Says ${assertion.statement}`
        : `Does not say ${assertion.statement}`;
  }
}

/**
 * What a verdict judged, for a run stored before its case's assertions were
 * kept with it. The claim itself is unknown: the case may have been edited
 * since, and the current wording is not what this run was judged against.
 */
const assertionKindLabels: Record<PlaybookTestAssertionKind, string> = {
  outcome: "An outcome assertion",
  tool_call: "A tool call assertion",
  fact_captured: "A fact captured assertion",
  content: "A content assertion",
  decision: "A decision assertion",
};

const countLabel = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

/**
 * The tallies a verdict carries, as one sentence. Null when the case carries no
 * assertions, which is graded on its outcome alone and has nothing to tally.
 */
export function describePlaybookTestVerdictTallies(
  verdict: PlaybookTestVerdict,
  assertionVerdicts: readonly PlaybookTestAssertionVerdict[],
): string | null {
  if (assertionVerdicts.length === 0) {
    return null;
  }

  const parts = [
    `${verdict.gating_passed} of ${countLabel(verdict.gating_applicable, "assertion", "assertions")} passed`,
  ];

  const notApplicable = assertionVerdicts.filter(
    (assertionVerdict) => !assertionVerdict.applicable,
  ).length;
  if (notApplicable > 0) {
    parts.push(`${notApplicable} not applicable`);
  }

  if (verdict.diagnostics_total > 0) {
    parts.push(
      `${verdict.diagnostics_passed} of ${verdict.diagnostics_total} diagnostic passed`,
    );
  }

  if (verdict.node_incorrect > 0) {
    parts.push(
      `${countLabel(verdict.node_incorrect, "grading expectation", "grading expectations")} incorrect`,
    );
  }

  return parts.join(", ");
}

interface AssertionVerdictRowProps {
  assertion: PlaybookTestAssertion | undefined;
  verdict: PlaybookTestAssertionVerdict;
}

const AssertionVerdictRow: FC<AssertionVerdictRowProps> = (props) => {
  const { assertion, verdict } = props;

  const renderIcon = () => {
    if (!verdict.applicable) {
      return <IconMinus className="size-4 shrink-0 text-muted-foreground" />;
    }
    return verdict.passed ? (
      <IconCheck className="size-4 shrink-0 text-success" />
    ) : (
      <IconX className="size-4 shrink-0 text-destructive" />
    );
  };

  return (
    <li className="flex items-start gap-2 py-2">
      <span className="mt-0.5">{renderIcon()}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground">
            {assertion
              ? describePlaybookTestAssertion(assertion)
              : assertionKindLabels[verdict.kind]}
          </span>
          {assertion?.diagnostic ? (
            <Pill label="Diagnostic" size={Size.SMALL} color={Color.Gray} />
          ) : null}
          {!verdict.applicable ? (
            <span className="text-xs text-muted-foreground">
              Not applicable
            </span>
          ) : null}
        </div>
        {verdict.evidence ? (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {verdict.evidence}
          </p>
        ) : null}
      </div>
    </li>
  );
};

export interface PlaybookTestVerdictSummaryProps {
  verdict: PlaybookTestVerdict;
  assertionVerdicts: readonly PlaybookTestAssertionVerdict[];
  /**
   * The case's assertions as the run was judged against them, which say what
   * each verdict judged. Empty for a run stored before they were kept, whose
   * verdicts then read on their evidence alone.
   */
  assertions: readonly PlaybookTestAssertion[];
}

/**
 * Whether a completed run passed and what the judge found, above the grading
 * output the run also produced.
 */
export const PlaybookTestVerdictSummary: FC<PlaybookTestVerdictSummaryProps> = (
  props,
) => {
  const { verdict, assertionVerdicts, assertions } = props;

  const assertionsById = new Map(
    assertions.map((assertion) => [assertion.id, assertion]),
  );
  const tallies = describePlaybookTestVerdictTallies(
    verdict,
    assertionVerdicts,
  );

  return (
    <section className="rounded-lg border border-border-light bg-white">
      <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
        <Pill
          label={verdict.passed ? "Passed" : "Failed"}
          size={Size.SMALL}
          color={verdict.passed ? Color.Green : Color.Red}
        />
        <span className="text-sm text-muted-foreground">
          {tallies ??
            "Graded on the expected outcome — this case makes no assertions."}
        </span>
      </div>

      {assertionVerdicts.length > 0 ? (
        <Disclosure className="border-t border-border-light">
          <Heading>
            <AriaButton
              slot="trigger"
              className="group w-full min-w-0 cursor-pointer text-left"
            >
              <div className="flex items-center gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1 text-sm font-medium text-foreground">
                  Assertions
                </div>
                <div className="text-sm text-muted-foreground">
                  {countLabel(assertionVerdicts.length, "claim", "claims")}
                </div>
                <IconChevronDown className="size-4 shrink-0 text-gray-400 transition-transform group-aria-expanded:rotate-180" />
              </div>
            </AriaButton>
          </Heading>
          <DisclosurePanel>
            <ul className="divide-y divide-border-light border-t border-border-light px-3">
              {assertionVerdicts.map((assertionVerdict) => (
                <AssertionVerdictRow
                  key={assertionVerdict.id}
                  assertion={assertionsById.get(assertionVerdict.id)}
                  verdict={assertionVerdict}
                />
              ))}
            </ul>
          </DisclosurePanel>
        </Disclosure>
      ) : null}
    </section>
  );
};
